import { observer } from '@formily/react';
import { IAnyObject, isVacuous } from '@yimoka/shared';
import { BaseStore, getEntityStore, IStore, IStoreConfig } from '@yimoka/store';
import React, { useEffect } from 'react';

import { useDeepMemo } from '../../hooks/deep-memo';
import { useEntityConfig } from '../../hooks/entity-config';

import { EntityValues, FetchDetail, IEntityValuesProps } from './detail';

export const EntityEdit = observer((props: IEntityEditProps) => {
  const { values, store, config, scope, entity, instanceKey, dispose, ...args } = props;
  const curConfig = useEntityConfig(config ?? entity?.config);

  const editStore = useDeepMemo(() => {
    if (store instanceof BaseStore) {
      return store;
    }
    if (entity) {
      return entity.getEdit({ ...(store ?? {}), instanceKey });
    }
    return getEntityStore(store, 'edit', curConfig);
  }, [store, entity, instanceKey, curConfig]);

  useEffect(() => {
    if (!dispose || !entity || store instanceof BaseStore) return;
    return () => {
      entity.disposeEdit(instanceKey);
    };
  }, [dispose, entity, store, instanceKey]);

  const curScope = useDeepMemo(() => ({ $entity: entity, ...scope }), [entity, scope]);

  if (isVacuous(values)) {
    return (
      <FetchDetail
        {...args}
        config={curConfig}
        scope={curScope}
        store={editStore}
      />
    );
  }

  return (
    <EntityValues
      {...args}
      config={curConfig}
      scope={{ ...curScope, $editStore: null }}
      store={editStore}
      values={values}
    />
  );
});

export type IEntityEditProps<V extends object = IAnyObject, R extends object = IAnyObject> = Partial<IEntityValuesProps<V, R>> & {
  detailStore?: IStore<V, R> | IStoreConfig<V, R>;
}
